/* cm-horizon-unified.js */
(() => {
  // =========================
  // CONFIG (sættes fra snippet)
  // =========================
  const ATC_BEHAVIOR = (window.cmAtcBehavior || 'cart'); // 'cart' | 'checkout'
  const CHECKOUT_URL = (window.cmCheckoutUrl || '/pages/order-checkout');

  const SEL = {
    // ATC knapper
    single: '.custom-single-link, .single-atc-button, .pricing-box__single-link',
    sub: '.subscription-atc-button',
    mm: '.mix-and-match-add-package-to-cart',

    // cart ui (tilpasset dit tema)
    drawer: 'cart-drawer-component',
    items: 'cart-drawer-component cart-items-component[data-section-id]',
    bubble: '#cart-icon-bubble, .cart-icon-bubble',
    subBox: '#subscription-purchase'
  };

  // ==================================================
  // OPTIONAL: Direct checkout via data-cm-direct-checkout
  // ==================================================
  function shouldDirectCheckout(btn) {
    return btn?.dataset?.cmDirectCheckout === 'true' || window.cmDirectCheckout === true;
  }

  function isPrimaryProductButton(btn) {
    const main = document.querySelector('#MainContent');
    return Boolean(
      btn &&
      main?.dataset.template?.startsWith('product') &&
      main.contains(btn) &&
      btn.closest('.product-information') &&
      !btn.closest('product-card, dialog, .quick-add')
    );
  }

  // --- RedirectGuard: forhindrer apps/tema i at sende til /cart ---
  const RedirectGuard = {
    _active: false,
    _origAssign: null,
    _origReplace: null,
    enable() {
      if (this._active) return;
      this._active = true;

      this._origAssign = window.location.assign;
      this._origReplace = window.location.replace;

      const allow = (url) => {
        try {
          const u = typeof url === 'string' ? url : (url?.toString?.() ?? '');
          if (/\/cart(?:\/|\?|#|$)/.test(u)) return false;
          // checkout må gerne ske, hvis vi *vil* til checkout
          if (ATC_BEHAVIOR === 'checkout' && /\/checkout(?:\/|\?|#|$)/.test(u)) return true;
        } catch {}
        return true;
      };

      window.location.assign = function (url) {
        if (!allow(url)) return;
        return RedirectGuard._origAssign.call(this, url);
      };

      window.location.replace = function (url) {
        if (!allow(url)) return;
        return RedirectGuard._origReplace.call(this, url);
      };

      document.addEventListener('click', RedirectGuard._linkBlocker, true);
    },
    disable() {
      if (!this._active) return;
      this._active = false;

      if (this._origAssign) window.location.assign = this._origAssign;
      if (this._origReplace) window.location.replace = this._origReplace;

      document.removeEventListener('click', RedirectGuard._linkBlocker, true);
    },
    _linkBlocker(e) {
      const a = e.target.closest('a[href]');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (/\/cart(?:\/|\?|#|$)/.test(href)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
      }
    }
  };

  // --- AJAX add (enkelt variant) ---
  async function addToCart(variantId, qty = 1, properties) {
    const payload = { id: Number(variantId), quantity: Number(qty) };
    if (properties && typeof properties === 'object') payload.properties = properties;

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'same-origin'
    });

    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        msg = j?.description || msg;
      } catch {}
      throw new Error(msg);
    }
    return res.json();
  }

  // --- Cart helpers ---
  async function getCartCount() {
    const r = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!r.ok) return null;
    const j = await r.json();
    return j?.item_count ?? null;
  }

  async function waitForCartChange(beforeCount, timeoutMs = 6000, intervalMs = 250) {
    const t0 = Date.now();
    for (;;) {
      const n = await getCartCount().catch(() => null);
      if (typeof n === 'number' && (beforeCount == null || n > beforeCount)) return n;
      if (Date.now() - t0 > timeoutMs) return n;
      await new Promise(r => setTimeout(r, intervalMs));
    }
  }

  // --- Drawer refresh ---
  async function refreshCartUI() {
    const itemsHost = document.querySelector(SEL.items);
    if (!itemsHost) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    const sectionId = itemsHost.getAttribute('data-section-id');
    const res = await fetch(`/?section_id=${encodeURIComponent(sectionId)}`, { credentials: 'same-origin' });
    if (!res.ok) {
      console.warn('[cm] section fetch fejlede', res.status);
      return;
    }

    const htmlText = await res.text();
    const tpl = document.createElement('template');
    tpl.innerHTML = htmlText;

    const freshItems = tpl.content.querySelector('cart-items-component[data-section-id]');
    if (!freshItems) {
      console.warn('[cm] kunne ikke finde cart-items-component i response');
      return;
    }

    const drawer = document.querySelector(SEL.drawer);
    const wasOpen = drawer?.querySelector('dialog')?.open;

    itemsHost.outerHTML = freshItems.outerHTML;

    // bubble refresh (best effort)
    try {
      const bubble = document.querySelector(SEL.bubble);
      const bubbleSectionId = bubble?.getAttribute?.('data-section-id');
      if (bubbleSectionId) {
        const r2 = await fetch(`/?section_id=${encodeURIComponent(bubbleSectionId)}`, { credentials: 'same-origin' });
        if (r2.ok) {
          const t2 = await r2.text();
          const tpl2 = document.createElement('template');
          tpl2.innerHTML = t2;
          const freshBubble = tpl2.content.querySelector(SEL.bubble);
          if (freshBubble && bubble) bubble.outerHTML = freshBubble.outerHTML;
        }
      }
    } catch (_) {}

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:add', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

    if (wasOpen && typeof drawer?.open === 'function') {
      drawer.open();
    }
  }

  async function openCartDrawer() {
    const drawer = document.querySelector(SEL.drawer);
    if (!drawer) return;

    if (typeof drawer.open === 'function') {
      try { drawer.open(); return; } catch {}
    }

    document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));

    const togglers = document.querySelectorAll(
      '[data-cart-drawer-toggle], [aria-controls="cart-drawer"], .header__icon--cart, .js-open-cart'
    );
    for (const t of togglers) {
      try { t.click(); return; } catch {}
    }
  }

  // --- Flow (enkelt variant) ---
  async function handleFlow(variantId, senderEl) {
    if (!variantId) { console.warn('[cm] Mangler variantId'); return; }

    RedirectGuard.enable();
    senderEl?.classList?.add('cm-is-loading');

    try {
      await addToCart(variantId, 1);

      if (ATC_BEHAVIOR === 'checkout') {
        window.location.href = CHECKOUT_URL;
        return;
      }

      await refreshCartUI();
      await openCartDrawer();
    } catch (e) {
      console.error('[cm] addToCart fejl:', e);
    } finally {
      senderEl?.classList?.remove('cm-is-loading');
      setTimeout(() => RedirectGuard.disable(), 300);
    }
  }

  // --- Mix & Match flow (pakke tilføjes af andet script) ---
  async function handleMixAndMatchFlow(senderEl) {
    RedirectGuard.enable();
    senderEl?.classList?.add('cm-is-loading');

    try {
      const before = await getCartCount().catch(() => null);

      // Hvis knappen har data-items (JSON), kan vi selv add'e:
      const itemsJson = senderEl?.getAttribute?.('data-items');
      if (itemsJson) {
        try {
          const items = JSON.parse(itemsJson);
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items }),
            credentials: 'same-origin'
          });
        } catch (e) {
          console.warn('[cm] data-items ikke gyldig JSON – falder tilbage til watcher', e);
        }
      }

      await waitForCartChange(before);

      if (ATC_BEHAVIOR === 'checkout') {
        window.location.href = CHECKOUT_URL;
        return;
      }

      await refreshCartUI();
      await openCartDrawer();
    } catch (e) {
      console.error('[cm] mix&match flow fejl:', e);
    } finally {
      senderEl?.classList?.remove('cm-is-loading');
      setTimeout(() => RedirectGuard.disable(), 300);
    }
  }

  // --- Normalisér knapper ---
  function normalizeButtons() {
    document.querySelectorAll(SEL.sub).forEach(btn => {
      if (btn.tagName === 'INPUT' && btn.type.toLowerCase() === 'submit') btn.type = 'button';
      if (btn.tagName === 'BUTTON' && (btn.getAttribute('type') || '').toLowerCase() !== 'button') {
        btn.setAttribute('type', 'button');
      }
    });

    document.querySelectorAll(SEL.mm).forEach(btn => {
      if ((btn.getAttribute('type') || '').toLowerCase() !== 'button') btn.setAttribute('type', 'button');
    });

    document.querySelectorAll('.single-atc-button').forEach(btn => {
      if ((btn.getAttribute('type') || '').toLowerCase() !== 'button') btn.setAttribute('type', 'button');
    });
  }

  // ==========================
  // BUY BOX: info toggle + UI
  // ==========================
  function applyBuyBoxState(wrapper) {
    const singleRadio = wrapper.querySelector('.buy-box-single-radio');
    const subRadio = wrapper.querySelector('.buy-box-subscription-radio');
    const singleChecked = !!singleRadio?.checked;
    const subChecked = !!subRadio?.checked;

    const singleBox = wrapper.querySelector('#single-purchase');
    const subBox = wrapper.querySelector('#subscription-purchase');
    const singleInfo = singleBox?.querySelector('.toggled_information');
    const subInfo = subBox?.querySelector('.toggled_information');

    singleBox?.style.setProperty('border', '1px solid #dcdce6');
    subBox?.style.setProperty('border', '1px solid #dcdce6');

    if (singleChecked) {
      if (singleInfo) singleInfo.style.display = '';
      if (subInfo) subInfo.style.display = 'none';
      if (singleBox) singleBox.style.border = '1px solid black';
    } else if (subChecked) {
      if (singleInfo) singleInfo.style.display = 'none';
      if (subInfo) subInfo.style.display = '';
      if (subBox) subBox.style.border = '1px solid black';
    }
  }

  function bindBuyBoxToggles() {
    document.querySelectorAll('.custom-buy-box-wrapper').forEach(wrapper => {
      applyBuyBoxState(wrapper);

      wrapper.addEventListener('change', (e) => {
        if (e.target.matches('.buy-box-single-radio, .buy-box-subscription-radio')) {
          applyBuyBoxState(wrapper);
        }
      });

      wrapper.addEventListener('click', (e) => {
        if (e.target.closest('.buy-box-single-radio') || e.target.closest('.buy-box-subscription-radio')) {
          setTimeout(() => applyBuyBoxState(wrapper), 0);
        }
      });
    });
  }

  // klik på hele boksen vælger radio
  function bindClickToSelectRadio() {
    document.querySelectorAll('.custom-buy-box').forEach(box => {
      box.addEventListener('click', (e) => {
        const radio = box.querySelector('input[type="radio"]');
        if (!radio) return;
        if (e.target === radio) return;

        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  // ==========================
  // MAIN BINDINGS (ATC handler)
  // ==========================
  function bindOnce() {
    if (document.documentElement.__cmHorizonBound) return;
    document.documentElement.__cmHorizonBound = true;

    // Direct checkout click hook (valgfri)
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;
      if (!isPrimaryProductButton(btn)) return;
      if (!shouldDirectCheckout(btn)) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      try {
        const ok = await window.addToCartFromButton?.(btn);
        if (ok) window.location.assign('/checkout');
      } catch (err) {
        console.error('[cm] direct checkout fejl:', err);
      }
    }, true);

    // Horizon ATC flow
    document.addEventListener('click', (e) => {
      const subBtn = e.target.closest(SEL.sub);
      const single = e.target.closest(SEL.single);
      const mmBtn = e.target.closest(SEL.mm);

      if (!subBtn && !single && !mmBtn) return;
      if (!isPrimaryProductButton(subBtn || single || mmBtn)) return;

      if (mmBtn) {
        // lad parcel-scriptet (eller andet) køre sit /cart/add.js først
        setTimeout(() => handleMixAndMatchFlow(mmBtn), 50);
        return;
      }

      e.preventDefault();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      e.stopPropagation();

      let variantId = null;

      if (subBtn) {
        variantId =
          subBtn.getAttribute('variant_id') ||
          document.querySelector(SEL.subBox)?.getAttribute('purchase-id');
        handleFlow(variantId, subBtn);
        return;
      }

      if (single) {
        variantId =
          single.dataset.singlePurchase ||
          single.getAttribute('data-single-purchase') ||
          single.getAttribute('variant_id');
        handleFlow(variantId, single);
      }
    }, true);

    // submit-capture (hvis noget stadig prøver form submit)
    document.addEventListener('submit', (e) => {
      const submitter = e.submitter || document.activeElement;
      const isSub = submitter?.closest?.(SEL.sub);
      const isSingle = submitter?.closest?.(SEL.single);
      const isMM = submitter?.closest?.(SEL.mm);
      if (!isSub && !isSingle && !isMM) return;
      if (!isPrimaryProductButton(isSub || isSingle || isMM)) return;

      e.preventDefault();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      e.stopPropagation();

      if (isMM) {
        setTimeout(() => handleMixAndMatchFlow(submitter), 50);
        return;
      }

      let variantId = null;
      if (isSub) {
        variantId =
          submitter.getAttribute('variant_id') ||
          document.querySelector(SEL.subBox)?.getAttribute('purchase-id');
        handleFlow(variantId, submitter);
      } else if (isSingle) {
        variantId = submitter.dataset?.singlePurchase || submitter.getAttribute('data-single-purchase');
        handleFlow(variantId, submitter);
      }
    }, true);
  }

  // ==========================
  // BOOT
  // ==========================
  function boot() {
    normalizeButtons();
    bindBuyBoxToggles();
    bindClickToSelectRadio();
  }

  document.addEventListener('DOMContentLoaded', () => {
    boot();
    bindOnce();
  });

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', boot);
})();
